import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Plus, Lock, Pencil, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useRoles } from '../hooks/useRoles';
import { useCanAccess } from '../hooks/useCanAccess';
import { useWorkgroups } from '../hooks/useWorkgroups';
import { PermissionGrid } from '../components/PermissionGrid';
import { RolesAPI, RoleSummary, RoleDetail } from '../api/roles';
import { cn } from '../components/ui/utils';

type ModulePermissionsState = { [modulecode: string]: { [action: string]: boolean } };

function toModulePermissions(permissions: { modulecode: string; actions: string[] }[]): ModulePermissionsState {
  const result: ModulePermissionsState = {};
  for (const perm of permissions) {
    result[perm.modulecode] = Object.fromEntries(perm.actions.map((a) => [a, true]));
  }
  return result;
}

function toPermissionList(state: ModulePermissionsState): { modulecode: string; actions: string[] }[] {
  return Object.entries(state)
    .map(([modulecode, actions]) => ({ modulecode, actions: Object.keys(actions).filter((a) => actions[a]) }))
    .filter((p) => p.actions.length > 0);
}

const ROLE_RANK: Record<string, number> = { OWNER: 0, ADMIN: 1, MEMBER: 2, VIEWER: 3 };

function sortRoles(roles: RoleSummary[]): RoleSummary[] {
  return [...roles].sort((a, b) => {
    const ra = ROLE_RANK[a.rolecode] ?? 99;
    const rb = ROLE_RANK[b.rolecode] ?? 99;
    if (ra !== rb) return ra - rb;
    return a.rolename.localeCompare(b.rolename);
  });
}

const ROLE_BLURBS: Record<string, string> = {
  OWNER: 'Billing and workgroup lifecycle. At least one Owner must exist at all times.',
  ADMIN: 'Day-to-day workgroup and delivery management, without billing or the ability to delete the workgroup.',
  MEMBER: 'Can create and run scans, manage their own work, and view most of the workgroup.',
  VIEWER: 'Read-only access across the workgroup.',
};

/**
 * Human-readable rows for the permission matrix, grouped in the same category
 * order as MODULE_CATALOG (api/libs/constants/default-roles.ts). "granted"
 * for a role requires ALL listed actions on that module -- for every row here
 * that maps to more than one action, every default role either has all of
 * them or none of them, so this is equivalent to an OR in practice today.
 */
const PERMISSION_MATRIX: { category: string; rows: { label: string; note?: string; modulecode: string; actions: string[] }[] }[] = [
  {
    category: 'Workgroup',
    rows: [
      { label: 'Rename or delete the workgroup', modulecode: 'workgroup', actions: ['update', 'delete'] },
      { label: 'Manage billing and plan', modulecode: 'billing', actions: ['update'] },
      { label: 'Transfer workgroup ownership', modulecode: 'workgroup', actions: ['transfer_ownership'] },
      { label: 'Invite or remove users', modulecode: 'teams', actions: ['invite', 'delete'] },
      { label: 'Assign or change roles', modulecode: 'teams', actions: ['assign'] },
      { label: 'Suspend or reactivate a user', modulecode: 'teams', actions: ['update'] },
      { label: 'Manage integrations (Jira, CI/CD)', modulecode: 'integrations', actions: ['manage'] },
      { label: 'Manage alerts, notifications and webhooks', modulecode: 'notifications', actions: ['manage'] },
      { label: 'View usage and quota', modulecode: 'usage', actions: ['read'] },
      { label: 'Create, edit or delete custom roles', modulecode: 'roles', actions: ['create', 'update', 'delete'] },
    ],
  },
  {
    category: 'Projects & Policy',
    rows: [
      { label: 'Create a project / application', modulecode: 'projects', actions: ['create'] },
      { label: 'Delete a project / application', note: 'Members later, for empty ones only', modulecode: 'projects', actions: ['delete'] },
      { label: 'Edit project settings', modulecode: 'projects', actions: ['update'] },
      { label: 'Add or remove project members', modulecode: 'projects', actions: ['update'] },
      { label: 'Create or edit a policy', modulecode: 'policies', actions: ['create', 'update'] },
      { label: 'Assign a policy to a project', modulecode: 'policies', actions: ['assign'] },
      { label: 'Configure evaluator rules', modulecode: 'policies', actions: ['configure_rules'] },
      { label: 'Request a policy exception', modulecode: 'policies', actions: ['request_exception'] },
      { label: 'Approve a policy exception', note: 'Restricted to Owner and Admin -- a Member approving their own exception would defeat the policy gate', modulecode: 'policies', actions: ['approve_exception'] },
    ],
  },
  {
    category: 'Scanning & Curation',
    rows: [
      { label: 'Trigger a scan', modulecode: 'scans', actions: ['create'] },
      { label: 'Cancel a running scan', note: 'Cancelling and deleting a scan are separate permissions', modulecode: 'scans', actions: ['cancel'] },
      { label: 'View scan results', modulecode: 'scans', actions: ['read'] },
      { label: 'Upload scan result files', modulecode: 'scans', actions: ['upload'] },
      { label: 'Delete a scan', note: 'Deletion is always a soft delete', modulecode: 'scans', actions: ['delete'] },
      { label: 'Restore a deleted scan', modulecode: 'scans', actions: ['restore'] },
    ],
  },
  {
    category: 'Reading & Reporting',
    rows: [
      { label: 'View components / SBOM', modulecode: 'components', actions: ['read'] },
      { label: 'Export component data', note: 'Not available on Community Edition', modulecode: 'components', actions: ['export'] },
      { label: 'View reports', modulecode: 'reports', actions: ['read'] },
      { label: 'Export a report', modulecode: 'reports', actions: ['export'] },
      { label: 'Create a custom report', modulecode: 'reports', actions: ['create'] },
      { label: 'Schedule a recurring report', modulecode: 'reports', actions: ['schedule'] },
    ],
  },
  {
    category: 'Tokens & Credentials',
    rows: [
      { label: 'Manage your own personal access tokens', modulecode: 'tokens', actions: ['read_own', 'manage_own'] },
      { label: "Revoke another user's tokens", modulecode: 'tokens', actions: ['revoke_others'] },
      { label: 'Manage service account tokens', modulecode: 'tokens', actions: ['manage_service_accounts'] },
    ],
  },
];

function roleGrants(detail: RoleDetail | undefined, modulecode: string, actions: string[]): boolean {
  if (!detail) return false;
  const granted = detail.permissions.find((p) => p.modulecode === modulecode)?.actions || [];
  return actions.every((a) => granted.includes(a));
}

/** Read-only "what does this role actually do" summary, grouped by module category so it reads as responsibilities rather than a raw permission grid. */
function RoleResponsibilities({ role, detail, loading }: { role: RoleSummary; detail?: RoleDetail; loading: boolean }) {
  if (loading || !detail) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-2" />
        Loading permissions...
      </div>
    );
  }

  if (role.rolecode === 'OWNER') {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
        Full access to every module and action in this workgroup, by design.
      </div>
    );
  }

  const grantedRows = PERMISSION_MATRIX.flatMap((c) => c.rows.map((r) => ({ ...r, category: c.category }))).filter((r) =>
    roleGrants(detail, r.modulecode, r.actions),
  );
  const categories = Array.from(new Set(grantedRows.map((r) => r.category)));

  if (categories.length === 0) {
    return <p className="py-3 text-sm text-muted-foreground">This role has no permissions granted yet.</p>;
  }

  return (
    <div className="space-y-3 py-1">
      {categories.map((category) => (
        <div key={category}>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{category}</div>
          <ul className="space-y-1 text-sm list-disc list-inside text-foreground">
            {grantedRows
              .filter((r) => r.category === category)
              .map((r) => (
                <li key={r.label}>{r.label}</li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PermissionMatrixSection({
  roles,
  roleDetails,
  loading,
  currentRolecode,
}: {
  roles: RoleSummary[];
  roleDetails: Record<number, RoleDetail>;
  loading: boolean;
  currentRolecode: string | null;
}) {
  const sortedRoles = useMemo(() => sortRoles(roles), [roles]);
  const currentRole = sortedRoles.find((r) => r.rolecode === currentRolecode);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <ShieldCheck className="w-4 h-4 text-primary" />
            Your role
          </div>
          <p className="text-sm text-muted-foreground">
            Acting as <span className="font-semibold text-foreground">{currentRole?.rolename || 'Member'}</span>
            {currentRolecode && ROLE_BLURBS[currentRolecode] ? ` — ${ROLE_BLURBS[currentRolecode]}` : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            {sortedRoles.map((role) => (
              <Badge
                key={role.id}
                variant={role.rolecode === currentRolecode ? 'default' : 'outline'}
                className={role.rolecode === currentRolecode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}
              >
                {role.rolename}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Your role comes from your membership in this workgroup. To act as a different role, ask an Owner or Admin to change your membership.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Permission matrix</CardTitle>
          <CardDescription>
            A filled dot means the permission is granted to that role. Permissions are granted at the workgroup level and
            inherited downward through Application, Project, Scan and Component.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Permission</TableHead>
                  {sortedRoles.map((role) => (
                    <TableHead key={role.id} className="text-center">
                      <div className={role.rolecode === currentRolecode ? 'text-primary font-semibold' : ''}>{role.rolename}</div>
                      {role.rolecode === currentRolecode && <div className="text-[10px] font-normal text-muted-foreground">you</div>}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {PERMISSION_MATRIX.map((group) => (
                  <Fragment key={group.category}>
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={sortedRoles.length + 1}
                        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40 py-1.5"
                      >
                        {group.category}
                      </TableCell>
                    </TableRow>
                    {group.rows.map((row) => (
                      <TableRow key={`${group.category}-${row.label}`}>
                        <TableCell className="whitespace-normal">
                          <div className="text-sm text-foreground">{row.label}</div>
                          {row.note && <div className="text-xs text-muted-foreground">{row.note}</div>}
                        </TableCell>
                        {sortedRoles.map((role) => {
                          const granted = roleGrants(roleDetails[role.id], row.modulecode, row.actions);
                          return (
                            <TableCell key={role.id} className="text-center">
                              <span
                                className={cn(
                                  'inline-block w-2.5 h-2.5 rounded-full',
                                  granted ? (role.rolecode === currentRolecode ? 'bg-primary' : 'bg-muted-foreground/70') : 'bg-transparent',
                                )}
                              >
                                {!granted && <span className="text-muted-foreground/50">–</span>}
                              </span>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function RolesManagement() {
  const { data: roles, modules, loading, deleteRole, createRole, updateRole, refetch } = useRoles();
  const { activeWorkgroup } = useWorkgroups();
  // Custom role management ("RBAC" on the pricing page) is entitlement-gated,
  // not just role-gated -- an Owner on a Community-tier workgroup still
  // shouldn't see these controls (see useCanAccess.ts).
  const canCreate = useCanAccess('roles', 'create');
  const canUpdate = useCanAccess('roles', 'update');
  const canDelete = useCanAccess('roles', 'delete');

  const [editingRole, setEditingRole] = useState<RoleSummary | 'new' | null>(null);
  const [rolecode, setRolecode] = useState('');
  const [rolename, setRolename] = useState('');
  const [description, setDescription] = useState('');
  const [modulePermissions, setModulePermissions] = useState<ModulePermissionsState>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RoleSummary | null>(null);

  const [roleDetails, setRoleDetails] = useState<Record<number, RoleDetail>>({});
  const [detailsLoading, setDetailsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (roles.length === 0) return;
    setDetailsLoading(true);
    Promise.all(
      roles.map(async (role) => {
        const response = await RolesAPI.getById(role.id);
        return [role.id, response.data] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<number, RoleDetail> = {};
      for (const [id, detail] of entries) {
        if (detail) next[id] = detail;
      }
      setRoleDetails(next);
      setDetailsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [roles]);

  const openCreate = () => {
    setRolecode('');
    setRolename('');
    setDescription('');
    setModulePermissions({});
    setEditingRole('new');
  };

  const openEdit = async (role: RoleSummary) => {
    setEditingRole(role);
    setRolecode(role.rolecode);
    setRolename(role.rolename);
    setDescription(role.description || '');
    if (roleDetails[role.id]) {
      setModulePermissions(toModulePermissions(roleDetails[role.id].permissions));
      return;
    }
    setIsLoadingDetail(true);
    try {
      const response = await RolesAPI.getById(role.id);
      if (response.data) {
        setModulePermissions(toModulePermissions(response.data.permissions));
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDialog = () => setEditingRole(null);

  const toggleAction = (modulecode: string, action: string) => {
    setModulePermissions((prev) => {
      const next = { ...prev };
      const current = { ...(next[modulecode] || {}) };
      if (current[action]) {
        delete current[action];
      } else {
        current[action] = true;
      }
      if (Object.keys(current).length === 0) {
        delete next[modulecode];
      } else {
        next[modulecode] = current;
      }
      return next;
    });
  };

  const toggleAllActions = (modulecode: string, actions: string[]) => {
    setModulePermissions((prev) => {
      const next = { ...prev };
      const current = next[modulecode] || {};
      const allEnabled = actions.every((a) => current[a]);
      if (allEnabled) {
        delete next[modulecode];
      } else {
        next[modulecode] = Object.fromEntries(actions.map((a) => [a, true]));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!rolename.trim() || (editingRole === 'new' && !rolecode.trim())) {
      toast.error('Role name is required');
      return;
    }

    setIsSaving(true);
    try {
      const permissions = toPermissionList(modulePermissions);
      const result =
        editingRole === 'new'
          ? await createRole({ rolecode: rolecode.trim().toUpperCase().replace(/\s+/g, '_'), rolename: rolename.trim(), description, permissions })
          : await updateRole((editingRole as RoleSummary).id, { rolename: rolename.trim(), description, permissions });

      if (result.success) {
        toast.success(result.message);
        closeDialog();
      } else {
        toast.error(result.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const result = await deleteRole(deleteTarget.id);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
    setDeleteTarget(null);
  };

  const isOwnerRole = editingRole !== 'new' && editingRole?.rolecode === 'OWNER';

  return (
    <div className="pt-3 space-y-6">
      <PermissionMatrixSection
        roles={roles}
        roleDetails={roleDetails}
        loading={loading || detailsLoading}
        currentRolecode={activeWorkgroup?.role || null}
      />

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Manage roles</h3>
          <p className="text-muted-foreground text-sm">
            Every workgroup starts with Owner, Admin, Member, and Viewer. Create a custom role if you need something narrower.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Role
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {role.rolename}
                      {role.issystem && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                    </CardTitle>
                    <CardDescription>{role.description || 'No description'}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{role.memberCount} member{role.memberCount !== 1 ? 's' : ''}</Badge>
                  <div className="flex gap-1">
                    {canUpdate && role.rolecode !== 'OWNER' && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(role)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && role.isdeletable && (
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(role)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="border-t pt-2">
                  <RoleResponsibilities role={role} detail={roleDetails[role.id]} loading={detailsLoading} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editingRole !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole === 'new' ? 'Create Role' : `Edit ${rolename}`}</DialogTitle>
            <DialogDescription>
              {isOwnerRole
                ? 'The Owner role always has full access and cannot be modified.'
                : 'Choose which actions this role can perform in each module.'}
            </DialogDescription>
          </DialogHeader>

          {isOwnerRole ? (
            <div className="text-center py-8">
              <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Owner grants every action on every module by design.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {editingRole === 'new' && (
                <div className="space-y-2">
                  <Label htmlFor="rolecode">Role code</Label>
                  <Input id="rolecode" value={rolecode} onChange={(e) => setRolecode(e.target.value)} placeholder="e.g. SECURITY_LEAD" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="rolename">Role name</Label>
                <Input id="rolename" value={rolename} onChange={(e) => setRolename(e.target.value)} placeholder="e.g. Security Lead" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this role for?" />
              </div>

              <PermissionGrid
                availableModules={modules}
                modulePermissions={modulePermissions}
                onToggleAction={toggleAction}
                onToggleAllActions={toggleAllActions}
                isLoading={isLoadingDetail}
              />
            </div>
          )}

          {!isOwnerRole && (
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Role'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Delete Role
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.rolename}"? Members must be reassigned to another role first.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RolesManagement;
